import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { PhoneOutlined, CloseOutlined, UserAddOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHeadset, 
  faBolt, 
  faBackspace, 
  faStar as faStarSolid,
  faClock,
  faAddressCard,
  faListCheck
} from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  CloseButton,
  ModalBody,
  LeftPanel,
  RightPanel,
  CallTypeToggle,
  CallTypeButton,
  SelectGroup,
  StyledSelect,
  SelectRow,
  PhoneDisplay,
  DialPad,
  DialButton,
  ActionRow,
  ActionButton,
  TabHeader,
  Tab,
  TabContent,
  EmptyState,
  ListItem,
  ListItemAvatar,
  ListItemContent,
  ListItemAction,
  COUNTRY_CODES
} from './styled';
import ContactPreviewCard from './ContactPreviewCard';
import TasksPanel from './TasksPanel';
import DialerService from '../../services/DialerService';

// Dial pad keys configuration
const DIAL_KEYS = [
  { number: '1', letters: '' },
  { number: '2', letters: 'ABC' },
  { number: '3', letters: 'DEF' },
  { number: '4', letters: 'GHI' },
  { number: '5', letters: 'JKL' },
  { number: '6', letters: 'MNO' },
  { number: '7', letters: 'PQRS' },
  { number: '8', letters: 'TUV' },
  { number: '9', letters: 'WXYZ' },
  { number: '*', letters: '' },
  { number: '0', letters: '+' },
  { number: '#', letters: '' },
];

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

// Format time ago
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const DialerModal = ({ 
  isOpen, 
  onClose, 
  onCall,
  initialPhone = '' 
}) => {
  // State for dial pad
  const [phoneNumber, setPhoneNumber] = useState(initialPhone);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]);
  
  // State for call type
  const [callType, setCallType] = useState('personal'); // 'personal' | 'agent'
  
  // State for selectors
  const [fromNumbers, setFromNumbers] = useState([]);
  const [selectedFromNumber, setSelectedFromNumber] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  
  // State for tabs
  const [activeTab, setActiveTab] = useState('recent');
  
  // State for recent calls & favorites (from localStorage)
  const [recentCalls, setRecentCalls] = useState([]);
  const [favorites, setFavorites] = useState([]);
  
  // State for contact matching
  const [matchedContact, setMatchedContact] = useState(null);
  const [isSearchingContact, setIsSearchingContact] = useState(false);
  
  // Refs
  const containerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  // Load from localStorage on mount
  useEffect(() => {
    if (isOpen) {
      const savedRecent = localStorage.getItem('recentDialerCalls');
      const savedFavorites = localStorage.getItem('dialerFavorites');
      
      if (savedRecent) {
        try {
          setRecentCalls(JSON.parse(savedRecent));
        } catch (e) {
          console.error('Error parsing recent calls:', e);
        }
      }
      
      if (savedFavorites) {
        try {
          setFavorites(JSON.parse(savedFavorites));
        } catch (e) {
          console.error('Error parsing favorites:', e);
        }
      }
      
      // Reset phone number if no initial value
      if (!initialPhone) {
        setPhoneNumber('');
      }
      
      // Fetch phone numbers and agents
      fetchPhoneNumbers();
      fetchAgents();
    }
  }, [isOpen, initialPhone]);
  
  // Fetch available phone numbers
  const fetchPhoneNumbers = async () => {
    try {
      const numbers = await DialerService.getPhoneNumbers();
      setFromNumbers(numbers);
      // Set default number
      const defaultNumber = numbers.find(n => n.isDefault) || numbers[0];
      setSelectedFromNumber(defaultNumber);
    } catch (error) {
      console.error('Error fetching phone numbers:', error);
      // Use fallback
      setFromNumbers([{ id: 'default', phoneNumber: '(469) 757-8744', isDefault: true }]);
      setSelectedFromNumber({ id: 'default', phoneNumber: '(469) 757-8744', isDefault: true });
    }
  };
  
  // Fetch available agents
  const fetchAgents = async () => {
    try {
      const agentList = await DialerService.getAgentTemplates();
      setAgents(agentList);
      if (agentList.length > 0) {
        setSelectedAgent(agentList[0]);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      // Use fallback
      setAgents([{ id: 'jane', name: 'Jane - Sales Specialist' }]);
      setSelectedAgent({ id: 'jane', name: 'Jane - Sales Specialist' });
    }
  };
  
  // Contact search with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    const digits = phoneNumber.replace(/\D/g, '');
    
    if (digits.length >= 7) {
      setIsSearchingContact(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const contact = await DialerService.searchContact(digits);
          setMatchedContact(contact);
          // Auto-switch to contact tab if found
          if (contact) {
            setActiveTab('contact');
          }
        } catch (error) {
          console.error('Error searching contact:', error);
          setMatchedContact(null);
        } finally {
          setIsSearchingContact(false);
        }
      }, 300);
    } else {
      setMatchedContact(null);
    }
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [phoneNumber]);
  
  // Handle keyboard events
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      // Escape to close
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      
      // Don't intercept if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      // Number keys
      if (/^[0-9*#]$/.test(e.key)) {
        e.preventDefault();
        handleDialPress(e.key);
      }
      
      // Backspace
      if (e.key === 'Backspace') {
        e.preventDefault();
        handleBackspace();
      }
      
      // Enter to call
      if (e.key === 'Enter' && phoneNumber.replace(/\D/g, '').length >= 7) {
        e.preventDefault();
        handleCall();
      }
      
      // Ctrl+V / Cmd+V for paste
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        // Let the paste happen naturally, then update
        setTimeout(handlePasteFromClipboard, 0);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, phoneNumber, onClose]);
  
  // Handle dial button press
  const handleDialPress = useCallback((digit) => {
    setPhoneNumber(prev => {
      const digits = prev.replace(/\D/g, '');
      if (digits.length >= 10) return prev;
      return digits + digit;
    });
  }, []);
  
  // Handle backspace
  const handleBackspace = useCallback(() => {
    setPhoneNumber(prev => {
      const digits = prev.replace(/\D/g, '');
      return digits.slice(0, -1);
    });
  }, []);
  
  // Handle double-click clear
  const handleClear = useCallback(() => {
    setPhoneNumber('');
    setMatchedContact(null);
  }, []);
  
  // Handle paste from clipboard
  const handlePasteFromClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      const digits = text.replace(/\D/g, '').slice(0, 10);
      if (digits) {
        setPhoneNumber(digits);
      }
    } catch (error) {
      console.error('Error pasting from clipboard:', error);
    }
  }, []);
  
  // Handle call initiation
  const handleCall = useCallback(() => {
    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length < 7) return;
    
    const fullNumber = `${selectedCountry.code}${digits}`;
    
    // Save to recent calls
    const callRecord = {
      id: Date.now().toString(),
      number: digits,
      fullNumber,
      countryCode: selectedCountry.code,
      countryFlag: selectedCountry.flag,
      timestamp: new Date().toISOString(),
      contactName: matchedContact?.name || null,
      callType,
      agentId: callType === 'agent' ? selectedAgent?.id : null,
      agentName: callType === 'agent' ? selectedAgent?.name : null,
      fromNumber: selectedFromNumber?.phoneNumber || null,
    };
    
    const updatedRecent = [callRecord, ...recentCalls.slice(0, 9)];
    setRecentCalls(updatedRecent);
    localStorage.setItem('recentDialerCalls', JSON.stringify(updatedRecent));
    
    // Call the callback
    if (onCall) {
      onCall(
        fullNumber,
        matchedContact,
        callType === 'agent' ? selectedAgent : null,
        callType,
        selectedFromNumber?.phoneNumber || null
      );
    }
    
    // Close modal
    onClose();
  }, [
    phoneNumber, 
    selectedCountry, 
    matchedContact, 
    callType, 
    selectedAgent, 
    selectedFromNumber, 
    recentCalls, 
    onCall, 
    onClose
  ]);
  
  // Handle favorite toggle
  const handleToggleFavorite = useCallback((item = null) => {
    const numberToToggle = item?.number || phoneNumber.replace(/\D/g, '');
    if (!numberToToggle || numberToToggle.length < 7) return;
    
    const existingIndex = favorites.findIndex(f => f.number === numberToToggle);
    let updatedFavorites;
    
    if (existingIndex >= 0) {
      // Remove from favorites
      updatedFavorites = favorites.filter(f => f.number !== numberToToggle);
    } else {
      // Add to favorites
      const favoriteRecord = {
        id: Date.now().toString(),
        number: numberToToggle,
        countryCode: item?.countryCode || selectedCountry.code,
        countryFlag: item?.countryFlag || selectedCountry.flag,
        contactName: item?.contactName || matchedContact?.name || null,
        addedAt: new Date().toISOString(),
      };
      updatedFavorites = [favoriteRecord, ...favorites];
    }
    
    setFavorites(updatedFavorites);
    localStorage.setItem('dialerFavorites', JSON.stringify(updatedFavorites));
  }, [favorites, phoneNumber, selectedCountry, matchedContact]);
  
  // Check if current number is favorited
  const isCurrentFavorited = favorites.some(
    f => f.number === phoneNumber.replace(/\D/g, '')
  );
  
  // Handle clicking a list item to populate dial pad
  const handleSelectNumber = useCallback((item) => {
    setPhoneNumber(item.number);
    const country = COUNTRY_CODES.find(c => c.code === item.countryCode) || COUNTRY_CODES[0];
    setSelectedCountry(country);
  }, []);
  
  // Phone number validation
  const digits = phoneNumber.replace(/\D/g, '');
  const canCall = digits.length >= 7;
  const canFavorite = digits.length >= 7;
  
  if (!isOpen) return null;
  
  const modalContent = (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContainer ref={containerRef} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <ModalHeader>
          <div className="header-title">
            <PhoneOutlined />
            Quick Call
          </div>
          <CloseButton onClick={onClose} title="Close (Esc)">
            <CloseOutlined />
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          {/* Left Panel - Dial Pad */}
          <LeftPanel>
            {/* Call Type Toggle */}
            <CallTypeToggle>
              <CallTypeButton
                $active={callType === 'personal'}
                $type="personal"
                onClick={() => setCallType('personal')}
                title="Make a personal call"
              >
                <FontAwesomeIcon icon={faHeadset} />
                Call Myself
              </CallTypeButton>
              <CallTypeButton
                $active={callType === 'agent'}
                $type="agent"
                onClick={() => setCallType('agent')}
                title="Have an AI agent make the call"
              >
                <FontAwesomeIcon icon={faBolt} />
                AI Agent Call
              </CallTypeButton>
            </CallTypeToggle>
            
            {/* From Number Selector */}
            <SelectGroup>
              <div className="select-label">From Number</div>
              <StyledSelect>
                <select
                  value={selectedFromNumber?.id || ''}
                  onChange={(e) => {
                    const number = fromNumbers.find(n => n.id === e.target.value);
                    setSelectedFromNumber(number);
                  }}
                >
                  {fromNumbers.map((number) => (
                    <option key={number.id} value={number.id}>
                      {number.phoneNumber} {number.isDefault ? '(Default)' : ''}
                      {number.friendlyName ? ` - ${number.friendlyName}` : ''}
                    </option>
                  ))}
                </select>
              </StyledSelect>
            </SelectGroup>
            
            {/* Country & Agent Row */}
            <SelectRow>
              <SelectGroup>
                <div className="select-label">Country</div>
                <StyledSelect>
                  <select
                    value={`${selectedCountry.country}-${selectedCountry.code}`}
                    onChange={(e) => {
                      const [country, code] = e.target.value.split('-');
                      const countryObj = COUNTRY_CODES.find(
                        c => c.country === country && c.code === code
                      );
                      if (countryObj) setSelectedCountry(countryObj);
                    }}
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={`${c.country}-${c.code}`} value={`${c.country}-${c.code}`}>
                        {c.flag} {c.code}
                      </option>
                    ))}
                  </select>
                </StyledSelect>
              </SelectGroup>
              
              {callType === 'agent' && (
                <SelectGroup>
                  <div className="select-label">Agent</div>
                  <StyledSelect>
                    <select
                      value={selectedAgent?.id || ''}
                      onChange={(e) => {
                        const agent = agents.find(a => a.id === e.target.value);
                        setSelectedAgent(agent);
                      }}
                    >
                      {agents.map((agent) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name}
                        </option>
                      ))}
                    </select>
                  </StyledSelect>
                </SelectGroup>
              )}
            </SelectRow>
            
            {/* Phone Number Display */}
            <PhoneDisplay onClick={handlePasteFromClipboard} title="Click to paste from clipboard">
              {digits ? (
                <>
                  <div className="phone-number">
                    <span className="country-code">{selectedCountry.code}</span>
                    {formatPhoneNumber(phoneNumber)}
                    <span className="cursor" />
                  </div>
                  {matchedContact && (
                    <div className="contact-match">
                      <UserAddOutlined />
                      {matchedContact.name}
                    </div>
                  )}
                </>
              ) : (
                <div className="placeholder">Enter number</div>
              )}
            </PhoneDisplay>
            
            {/* Dial Pad */}
            <DialPad>
              {DIAL_KEYS.map((key) => (
                <DialButton
                  key={key.number}
                  onClick={() => handleDialPress(key.number)}
                  title={key.number}
                >
                  <span className="number">{key.number}</span>
                  {key.letters && <span className="sub-text">{key.letters}</span>}
                </DialButton>
              ))}
            </DialPad>
            
            {/* Action Row */}
            <ActionRow>
              <ActionButton
                $variant="backspace"
                onClick={handleBackspace}
                onDoubleClick={handleClear}
                disabled={!digits}
                title="Backspace (double-click to clear)"
              >
                <FontAwesomeIcon icon={faBackspace} />
              </ActionButton>
              
              <ActionButton
                $variant="call"
                $size="large"
                onClick={handleCall}
                disabled={!canCall}
                title={canCall ? 'Make call (Enter)' : 'Enter at least 7 digits'}
              >
                <PhoneOutlined />
              </ActionButton>
              
              <ActionButton
                $variant="favorite"
                $active={isCurrentFavorited}
                onClick={() => handleToggleFavorite()}
                disabled={!canFavorite}
                title={isCurrentFavorited ? 'Remove from favorites' : 'Add to favorites'}
              >
                <FontAwesomeIcon icon={isCurrentFavorited ? faStarSolid : faStarRegular} />
              </ActionButton>
            </ActionRow>
          </LeftPanel>
          
          {/* Right Panel - Tabs */}
          <RightPanel>
            <TabHeader>
              <Tab
                $active={activeTab === 'recent'}
                onClick={() => setActiveTab('recent')}
              >
                <FontAwesomeIcon icon={faClock} />
                Recent
              </Tab>
              <Tab
                $active={activeTab === 'favorites'}
                onClick={() => setActiveTab('favorites')}
              >
                <FontAwesomeIcon icon={faStarSolid} />
                Favorites
              </Tab>
              <Tab
                $active={activeTab === 'contact'}
                onClick={() => setActiveTab('contact')}
              >
                <FontAwesomeIcon icon={faAddressCard} />
                Contact
              </Tab>
              <Tab
                $active={activeTab === 'tasks'}
                onClick={() => setActiveTab('tasks')}
              >
                <FontAwesomeIcon icon={faListCheck} />
                Tasks
              </Tab>
            </TabHeader>
            
            <TabContent>
              {/* Recent Tab */}
              {activeTab === 'recent' && (
                <>
                  {recentCalls.length === 0 ? (
                    <EmptyState>
                      <FontAwesomeIcon icon={faClock} />
                      <div className="title">No recent calls yet</div>
                      <div className="subtitle">
                        Your call history will appear here
                      </div>
                    </EmptyState>
                  ) : (
                    recentCalls.map((call) => (
                      <ListItem
                        key={call.id}
                        onClick={() => handleSelectNumber(call)}
                      >
                        <ListItemAvatar>
                          {call.countryFlag || '📞'}
                        </ListItemAvatar>
                        <ListItemContent>
                          <div className="primary">
                            {call.contactName || formatPhoneNumber(call.number)}
                          </div>
                          <div className="secondary">
                            {call.agentName && (
                              <span className="agent-name">{call.agentName}</span>
                            )}
                            {call.agentName && ' • '}
                            {formatTimeAgo(call.timestamp)}
                          </div>
                        </ListItemContent>
                        <ListItemAction
                          $active={favorites.some(f => f.number === call.number)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFavorite(call);
                          }}
                          title="Toggle favorite"
                        >
                          <FontAwesomeIcon
                            icon={favorites.some(f => f.number === call.number) 
                              ? faStarSolid 
                              : faStarRegular}
                          />
                        </ListItemAction>
                      </ListItem>
                    ))
                  )}
                </>
              )}
              
              {/* Favorites Tab */}
              {activeTab === 'favorites' && (
                <>
                  {favorites.length === 0 ? (
                    <EmptyState>
                      <FontAwesomeIcon icon={faStarSolid} />
                      <div className="title">No favorites yet</div>
                      <div className="subtitle">
                        Star a number to add it here
                      </div>
                    </EmptyState>
                  ) : (
                    favorites.map((fav) => (
                      <ListItem
                        key={fav.id}
                        onClick={() => handleSelectNumber(fav)}
                      >
                        <ListItemAvatar $color="#fffbe6" $textColor="#faad14">
                          <FontAwesomeIcon icon={faStarSolid} />
                        </ListItemAvatar>
                        <ListItemContent>
                          <div className="primary">
                            {fav.contactName || formatPhoneNumber(fav.number)}
                          </div>
                          <div className="secondary">
                            Added {formatTimeAgo(fav.addedAt)}
                          </div>
                        </ListItemContent>
                        <ListItemAction
                          $active={true}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFavorite(fav);
                          }}
                          title="Remove from favorites"
                        >
                          <FontAwesomeIcon icon={faStarSolid} />
                        </ListItemAction>
                      </ListItem>
                    ))
                  )}
                </>
              )}
              
              {/* Contact Tab */}
              {activeTab === 'contact' && (
                <ContactPreviewCard
                  contact={matchedContact}
                  isLoading={isSearchingContact}
                  phoneNumber={phoneNumber}
                />
              )}
              
              {/* Tasks Tab */}
              {activeTab === 'tasks' && (
                <TasksPanel onSelectNumber={handleSelectNumber} />
              )}
            </TabContent>
          </RightPanel>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
  
  // Render as portal to document.body
  return ReactDOM.createPortal(modalContent, document.body);
};

export default DialerModal;
